import { ColumnsType } from "antd/es/table";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { convert2label, shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { TableColumnsProps } from "@lib/types/General";
import { XAICallModel } from "@lib/types/xAI";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { GenderEnum } from "@lib/types/xAI";
import {
  IsDisabledTag,
  LanguageLabel,
} from "@lib/components/MiniComponents/Others";

type Props<T> = TableColumnsProps<T> & {
  handleSelect: (item: XAICallModel) => void;
  handleDelete: (item: XAICallModel) => void;
};

const getColumns = <T extends XAICallModel>(
  props: Props<T>,
): ColumnsType<T> => {
  const { handleSelect, handleDelete } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (v, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
      width: "180px",
      filterType: "search",
    },

    {
      title: "common.name",
      dataIndex: "name",
      key: "name",
      width: "200px",
      filterType: "search",
    },

    {
      title: "common.aiModelId",
      dataIndex: "aiModelId",
      key: "aiModelId",
      width: "200px",
      filterType: "search",
      render: (d) => <TableIdButton text={`#${shortenString(d, 30)}`} />,
    },

    {
      title: "common.isDisabled",
      dataIndex: "isDisabled",
      key: "isDisabled",
      width: "120px",
      render: (d) => <IsDisabledTag isDisabled={d} />,
    },

    {
      title: "common.language",
      dataIndex: "language",
      key: "language",
      width: "120px",
      render: (d) => <LanguageLabel languageCode={d} />,
    },

    {
      title: "common.gender",
      dataIndex: "gender",
      key: "gender",
      width: "120px",
      render: (d) =>
        d
          ? convert2label(
              Object.keys(GenderEnum).find(
                (key) => GenderEnum[key as keyof typeof GenderEnum] === d,
              ) || "",
            )
          : "-",
    },

    {
      title: "common.personality",
      dataIndex: "personality",
      key: "personality",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.purpose",
      dataIndex: "purpose",
      key: "purpose",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.description",
      dataIndex: "description",
      key: "description",
      width: "250px",
      filterType: "search",
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
    },

    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
    },

    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "150px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelect(record)}
            noLabel
          />
          <ActionButton
            type="delete"
            onClick={() => handleDelete(record)}
            itemName="sidebar.xaiCallModels"
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
